import { Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { v4 as uuidv4 } from 'uuid';
import { MediaAttemptJournal, type MediaAttempt, type MediaPreparation } from './MediaAttemptJournal';

let owner: string | undefined;
const getOwner = () => owner ??= uuidv4();
let identity: string | null = null;
let journal: MediaAttemptJournal | undefined;
let ownerReady: Promise<void> | undefined;

const getJournal = () => journal ??= new MediaAttemptJournal(new MMKV({ id: 'app-guincho-media-attempts' }), getOwner());
const lockName = (id: string) => `app-guincho-media-owner:${id}`;

const ensureOwner = (): Promise<void> => {
  if (Platform.OS !== 'web') return Promise.resolve();
  return ownerReady ??= new Promise<void>(resolve => {
    if (typeof navigator === 'undefined' || !navigator.locks) { resolve(); return; }
    // Lock vive enquanto a execucao existir; o navegador libera ao fechar/recarregar a aba.
    void navigator.locks.request(lockName(getOwner()), async () => {
      resolve();
      await new Promise<void>(() => {});
    }).catch(() => resolve());
  });
};

const isOwnerAlive = async (id: string): Promise<boolean> => {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined' || !navigator.locks) return true;
  return navigator.locks.request(lockName(id), { ifAvailable: true }, lock => !lock);
};

export const bindMediaAttemptIdentity = (companyId?: string | null, userId?: string | null): void => {
  identity = companyId && userId ? `${companyId}:${userId}` : null;
};

export const beginMediaAttempt = async (input: Pick<MediaAttempt, 'id' | 'checklistId' | 'fieldId' | 'source'>): Promise<void> => {
  const bound = identity;
  if (!bound) return;
  try {
    await ensureOwner();
    if (identity === bound) getJournal().start({ ...input, identity: bound });
  } catch { /* Marcador indisponivel nao impede o trabalho do usuario. */ }
};

type MediaFailure = { identity: string; attemptId: string; phase?: string; errorCode?: string };
const failureListeners = new Set<(failure: MediaFailure) => void>();
export const registerMediaFailureListener = (listener: (failure: MediaFailure) => void): (() => void) => {
  failureListeners.add(listener);
  return () => { failureListeners.delete(listener); };
};
const notifyFailure = (failure: MediaFailure): void => {
  failureListeners.forEach(listener => { try { listener(failure); } catch { /* Diagnóstico não bloqueia o salvamento. */ } });
};
export const retainMediaPreparation = (id: string, preparation: MediaPreparation): boolean => {
  if (!identity) return false;
  try { return getJournal().retainPreparation(id, identity, preparation); } catch { return false; }
};
export const claimMediaPreparations = async (companyId: string, userId: string): Promise<MediaAttempt[]> => {
  const bound = `${companyId}:${userId}`;
  if (identity !== bound) return [];
  await ensureOwner();
  const attempts = await getJournal().claimPreparations(bound, isOwnerAlive);
  return identity === bound ? attempts : [];
};

export const finishMediaAttempt = (id: string, failed = false, details?: { phase?: string; errorCode?: string }): void => {
  if (!identity) return;
  try { const finished = getJournal().finish(id, identity, failed, details);
    if (failed && finished) notifyFailure({ identity, attemptId: id, ...details }); } catch { /* Falha aberta. */ }
};

export const advanceMediaAttempt = (id: string, phase: string): void => {
  if (!identity) return;
  try { getJournal().advance(id, identity, phase); } catch { /* Falha aberta. */ }
};

export const flagMediaAttemptFailure = (input: { attemptId: string; checklistId: string; fieldId: string; phase?: string; errorCode?: string }): void => {
  if (!identity) return;
  try {
    getJournal().start({ id: input.attemptId, checklistId: input.checklistId, fieldId: input.fieldId,
      identity, source: 'upload' });
    const finished = getJournal().finish(input.attemptId, identity, true, { phase: input.phase, errorCode: input.errorCode });
    if (finished) notifyFailure({ identity, attemptId: input.attemptId, phase: input.phase, errorCode: input.errorCode });
  } catch { /* Falha relevante continua disponivel no historico quando possivel. */ }
};

export const recoverMediaAttempts = async (companyId: string, userId: string): Promise<MediaAttempt[]> => {
  const bound = `${companyId}:${userId}`;
  if (identity !== bound) return [];
  try {
    await ensureOwner();
    const attempts = await getJournal().recover(bound, isOwnerAlive);
    return identity === bound ? attempts : [];
  } catch { return []; }
};

export const acknowledgeMediaAttempts = (companyId: string, userId: string, ids: string[]): void => {
  if (identity !== `${companyId}:${userId}`) return;
  try { getJournal().acknowledge(identity, ids); } catch { /* Falha aberta. */ }
};
